import { dataSourceWholeItems } from '@/resources/data-source';
import { ElectronApi } from '@/services/electron.api';
import { Shape } from '@/services/shape';
import { WatchfaceService } from '@/services/watchface';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { pickDir } from '@/utils/fs/pick-file.utils';
import { Rive } from '@rive-app/canvas';
import { Modal } from 'ant-design-vue';
import { useShapeManage } from './use-shape-manage';
import { dirToMd5 } from '@/utils/md5.utils';

export type RiveFileType = 'normal' | 'ext';

export const useShapeRive = () => {
  const watchfaceStore = useWatchfaceStore();
  const { toCenter } = useShapeManage();

  const stateMachine = 'State Machine 1';
  const readRiveInfo = async (riveFilename: string) => {
    const { fs, path } = ElectronApi;
    if (!fs || !path) return;

    const canvasOffscreen = new OffscreenCanvas(1, 1);
    const fileBf = await fs.promises.readFile(riveFilename);

    const dataSourceKeys: Set<string> = new Set();
    dataSourceWholeItems.forEach((item) => {
      dataSourceKeys.add(item.key);
    });

    return new Promise<{
      width: number;
      height: number;
      machineNames: string[];
      sourceKeys: string[];
      interactiveState: boolean;
    }>((resolve, reject) => {
      try {
        const r = new Rive({
          canvas: canvasOffscreen,
          buffer: fileBf.buffer as ArrayBuffer,
          stateMachines: stateMachine,
          autoplay: true,

          onLoad: () => {
            const width = r.artboardWidth;
            const height = r.artboardHeight;
            const names = r.stateMachineNames;
            const sourceKeys: string[] = [];
            let interactiveState = false;
            r.stateMachineInputs(r.stateMachineNames?.[0])?.forEach((item) => {
              const { name } = item;
              // 数据源
              if (dataSourceKeys.has(name)) {
                sourceKeys.push(name);
              }
              if (name === 'interactiveState') {
                interactiveState = true;
              }
            });
            r.stopRendering();
            resolve({
              width,
              height,
              machineNames: names,
              sourceKeys,
              interactiveState
            });
          }
        });
      } catch (error) {
        reject();
      }
    });
  };

  const validRiveFile = (data: {
    width: number;
    height: number;
    machineNames: string[];
    interactiveState: boolean;
  }) => {
    const { width, height, machineNames } = data;
    const { width: deviceWidth, height: deviceHeight } = watchfaceStore.size;
    if (deviceWidth < width || deviceHeight < height) {
      Modal.error({
        title: `Rive动效分辨率过大（${width}*${height}）`,
        content: `视频分辨率不可超过设备分辨率：${deviceWidth}*${deviceHeight}`
      });
      return false;
    }
    // 判断是否存在状态机
    if (machineNames?.[0] !== stateMachine) {
      Modal.error({
        title: `状态机名称错误`,
        content: `状态机名称必须为${stateMachine}, 现在是${machineNames?.[0]}，请检查 riv 文件`
      });
      return false;
    }
    if (machineNames.length > 1) {
      Modal.error({
        title: `状态机错误`,
        content: `仅需一个名称为${stateMachine}的状态机即可`
      });
      return false;
    }

    return true;
  };

  /**
   * 验证rive文件夹是否正确
   * @param data
   * @returns
   */
  const readRiveDir = async (riveDir: string) => {
    const { fs, path } = ElectronApi;
    if (!fs || !path) return;

    const filenames = await fs.promises.readdir(riveDir);
    filenames.sort();

    const res: string[] = [];

    filenames.forEach((basename) => {
      const extname = path.extname(basename);
      const filename = path.join(riveDir, basename);
      if (['.riv', '.png', '.ttf', '.otf'].includes(extname)) {
        res.push(filename);
      }
    });

    return res;
  };

  /**
   * 这是一个异步函数，用于选择然后上传 Rive 目录。
   *
   * @async
   * @function pickThenUploadRiveDir
   * @returns {Promise<void>} 返回一个 Promise，表示操作完成。
   */
  const pickThenUploadRiveDir = async (
    shape: Shape,
    riveType: RiveFileType
  ) => {
    const { path } = ElectronApi;
    if (!path) return;

    // 选择mp4文件
    const riveDir = await pickDir();
    if (!riveDir) return;

    // 读取rive目录，获取rive和图片
    const files = (await readRiveDir(riveDir)) ?? [];
    const rives = files.filter((item) => path.extname(item) === '.riv');
    if (rives?.length !== 1) {
      Modal.error({
        title: `Rive文件夹错误`,
        content: `文件夹内必须包含一个.riv文件`
      });
      return false;
    }

    // 验证rive文件数据
    const rive = rives?.[0];
    const riveInfo = await readRiveInfo(rive);
    if (!riveInfo || !validRiveFile(riveInfo)) return;

    // 将文件移动到目录中
    const watchfaceService = new WatchfaceService();
    const _riveDirs = [
      riveType == 'ext' ? '_riveExt' : '_rive',
      (await dirToMd5(riveDir)) ?? path.basename(riveDir)
    ];

    let riveFile = '';
    const resources: string[] = [];
    for (const file of files) {
      const isRive = file.endsWith('.riv');
      const _basename = await watchfaceService.resource.addFile(file, {
        subDirs: _riveDirs,
        // rive文件需要md5，其他文件不需要
        md5: isRive ? true : false
      });
      if (!_basename) continue;
      if (isRive) {
        riveFile = _basename;
      } else {
        resources.push(_basename);
      }
    }

    // 获取尺寸
    // const { videoWidth = 0, videoHeight = 0 } =
    //   (await getVideoData(videoFile)) ?? {};

    // 根据不同的rive类型，设备不同的字段
    const { riveFilekey, riveResourceskey, riveDiranme, riveInteractiveState } =
      getRiveAttrKeys(riveType);

    const interactiveState = riveInfo.interactiveState;

    shape.setAttrs({
      [riveFilekey]: riveFile,
      [riveResourceskey]: resources,
      [riveDiranme]: path.basename(riveDir),
      [riveInteractiveState]: interactiveState,
      w: riveInfo.width,
      h: riveInfo.height,
      riveToShow: riveType
    });

    toCenter(shape);
    return true;
  };

  const getRiveAttrKeys = (riveType: RiveFileType) => {
    const riveFilekey = riveType == 'ext' ? 'riveFileExt' : 'riveFile';
    const riveResourceskey =
      riveType == 'ext' ? 'riveResourcesExt' : 'riveResources';
    const riveDiranme = riveType == 'ext' ? 'riveDiranmeExt' : 'riveDiranme';
    const riveInteractiveState =
      riveType == 'ext' ? 'interactiveStateExt' : 'interactiveState';
    return {
      riveFilekey,
      riveResourceskey,
      riveDiranme,
      riveInteractiveState
    };
  };

  const removeRiveFile = (shape: Shape, riveType: RiveFileType) => {
    const { riveFilekey, riveResourceskey, riveDiranme, riveInteractiveState } =
      getRiveAttrKeys(riveType);

    shape.setAttrs({
      [riveFilekey]: '',
      [riveResourceskey]: [],
      [riveDiranme]: '',
      [riveInteractiveState]: false
    });
    // 如果没有rive和rive高级文件，则重置大小和位置
    if (!shape.attrs.riveFile && !shape.attrs.riveFileExt) {
      shape.setAttrs({
        w: 100,
        h: 100
      });
      toCenter(shape);
    }
  };

  return {
    pickThenUploadRiveDir,
    removeRiveFile
  };
};
